'use client';

import { PageSpecific } from '.generated/Feature.CW.model';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { ComponentProps } from 'lib/component-props';
import {
  Field,
  ImageField,
  LinkField,
  RichTextField,
  TextField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import Link from 'next/link';
import { Data } from '.generated/Foundation.CW.model';
import { TransformedSiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { SvgIcon } from 'helpers/SvgIcon';
import { formatBlogDate } from 'lib/utils/utilityFunction';
import useIsEditing from 'lib/hooks/useIsEditing';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';

export type FeaturedStoriesProps = ComponentProps &
  PageSpecific.FeaturedStories &
  Data.PageSpecific.Author;

interface FeaturedArticleItem {
  fields?: {
    heroImage?: ImageField;
    blogContentType?: TextField;
    blogPostTitle?: TextField;
    previewText?: RichTextField;
    author?: Data.PageSpecific.Author;
    date?: Field<string>;
  };
  url?: string;
}

const FeaturedStories = (props: FeaturedStoriesProps) => {
  return <ExpandedVersion {...props} />;
};
export default FeaturedStories;

export const ExpandedVersion = (props: FeaturedStoriesProps) => {
  const isEditing = useIsEditing();
  const fullArticleCard = props?.fields?.featuredArticleList?.[0] as FeaturedArticleItem;
  const authorCardFields = fullArticleCard?.fields?.author;

  const secondaryArticleCard1 = props?.fields?.featuredArticleList?.[1] as FeaturedArticleItem;
  const secondaryArticleCard2 = props?.fields?.featuredArticleList?.[2] as FeaturedArticleItem;

  //   Blog search page link
  const { sitecoreContext } = useSitecoreContext();
  const siteSettingsField = sitecoreContext?.siteSettings as TransformedSiteSettings;
  const blogSearchPage = siteSettingsField?.blogSearchPage as LinkField;

  //   Date Format
  const fullCardDateString = fullArticleCard?.fields?.date;
  const secondaryCard1DateString = secondaryArticleCard1?.fields?.date;
  const secondaryCard2DateString = secondaryArticleCard2?.fields?.date;

  return (
    <section className="max-w-screen-xxl mx-auto py-[80px] w-full lg:px-[80px] px-10" role="group">
      <PlainTextWrapper
        tag="h2"
        field={props?.fields?.featuredStoriesTitle}
        className="pb-12 text-36 leading-43 font-bold font-centuryGothic"
      />
      <article
        role="region"
        aria-labelledby={`${fullArticleCard?.fields?.blogPostTitle?.value}`}
        className={`rounded-md overflow-hidden flex flex-col group shadow-default hover:shadow-hover`}
      >
        {isEditing ? (
          <div className="w-full aspect-[7/4] shrink-0 max-h-[550px]">
            <ImageWrapper
              field={fullArticleCard?.fields?.heroImage}
              className="w-full h-full object-cover object-left-top"
            />
          </div>
        ) : (
          <Link
            href={fullArticleCard?.url || ''}
            className="w-full aspect-[7/4] shrink-0 max-h-[550px]"
          >
            <ImageWrapper
              field={fullArticleCard?.fields?.heroImage}
              className="w-full h-full object-cover object-left-top"
            />
          </Link>
        )}

        <div className="flex flex-col p-12">
          <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
            {isEditing ? (
              <PlainTextWrapper
                tag="p"
                field={fullArticleCard?.fields?.blogContentType}
                className=" text-blue-base font-bold "
              />
            ) : (
              <Link
                href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${fullArticleCard?.fields?.blogContentType?.value}`}
              >
                <PlainTextWrapper
                  tag="p"
                  field={fullArticleCard?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              </Link>
            )}

            <div className="text-[#111111]">{formatBlogDate(fullCardDateString?.value)}</div>
          </div>

          <div className="flex justify-between">
            {isEditing ? (
              <div className="flex-1">
                <PlainTextWrapper
                  tag="h3"
                  field={fullArticleCard?.fields?.blogPostTitle}
                  className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                />
              </div>
            ) : (
              <Link href={fullArticleCard?.url || ''} className="flex-1">
                <PlainTextWrapper
                  tag="h3"
                  field={fullArticleCard?.fields?.blogPostTitle}
                  className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                />
              </Link>
            )}

            <SvgIcon
              icon="arrow-right"
              size="lg"
              className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            />
          </div>

          <RichTextWrapper
            field={fullArticleCard?.fields?.previewText}
            className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
          />
          {(fullArticleCard?.fields?.author?.fields?.authorTitle?.value !== undefined ||
            fullArticleCard?.fields?.author?.fields?.authorName?.value !== undefined) && (
            <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
              {isEditing ? (
                <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                  <PlainTextWrapper field={fullArticleCard?.fields?.author?.fields?.authorName} />
                </div>
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?authorName=${authorCardFields?.fields?.authorName?.value}`}
                  className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                >
                  <PlainTextWrapper field={fullArticleCard?.fields?.author?.fields?.authorName} />
                </Link>
              )}

              {fullArticleCard?.fields?.author?.fields?.authorTitle?.value !== undefined &&
                fullArticleCard?.fields?.author?.fields?.authorName?.value !== undefined &&
                '|'}

              <PlainTextWrapper
                tag="p"
                field={fullArticleCard?.fields?.author?.fields?.authorTitle}
                className=""
              />
            </div>
          )}
        </div>
      </article>

      {/* Secondary feature Cards */}
      <div className="flex flex-col lg:flex-row gap-[20px] mt-16 w-full">
        <article
          role="region"
          aria-labelledby={`${secondaryArticleCard1?.fields?.blogPostTitle?.value}`}
          className={`rounded-md overflow-hidden lg:w-1/2 flex flex-col group shadow-default hover:shadow-hover`}
        >
          {isEditing ? (
            <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
              <ImageWrapper
                field={secondaryArticleCard1?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </div>
          ) : (
            <Link
              href={secondaryArticleCard1?.url || ''}
              className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
            >
              <ImageWrapper
                field={secondaryArticleCard1?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </Link>
          )}

          <div className="flex flex-col p-12">
            <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
              {isEditing ? (
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard1?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${secondaryArticleCard1?.fields?.blogContentType?.value}`}
                >
                  <PlainTextWrapper
                    tag="p"
                    field={secondaryArticleCard1?.fields?.blogContentType}
                    className=" text-blue-base font-bold "
                  />
                </Link>
              )}

              <div className="text-[#111111]">
                {formatBlogDate(secondaryCard1DateString?.value)}
              </div>
            </div>

            <div className="flex justify-between">
              {isEditing ? (
                <div className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard1?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </div>
              ) : (
                <Link href={secondaryArticleCard1?.url || ''} className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard1?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </Link>
              )}

              <SvgIcon
                icon="arrow-right"
                size="lg"
                className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              />
            </div>

            <RichTextWrapper
              field={secondaryArticleCard1?.fields?.previewText}
              className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
            />
            {(secondaryArticleCard1?.fields?.author?.fields?.authorTitle?.value !== undefined ||
              secondaryArticleCard1?.fields?.author?.fields?.authorName?.value !== undefined) && (
              <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                {isEditing ? (
                  <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                    <PlainTextWrapper
                      field={secondaryArticleCard1?.fields?.author?.fields?.authorName}
                    />
                  </div>
                ) : (
                  <Link
                    href={`${blogSearchPage?.value?.href}?authorName=${secondaryArticleCard1?.fields?.author?.fields?.authorName?.value}`}
                    className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                  >
                    <PlainTextWrapper
                      field={secondaryArticleCard1?.fields?.author?.fields?.authorName}
                    />
                  </Link>
                )}

                {secondaryArticleCard1?.fields?.author?.fields?.authorTitle?.value !== undefined &&
                  secondaryArticleCard1?.fields?.author?.fields?.authorName?.value !== undefined &&
                  '|'}

                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard1?.fields?.author?.fields?.authorTitle}
                  className=""
                />
              </div>
            )}
          </div>
        </article>
        <article
          role="region"
          aria-labelledby={`${secondaryArticleCard2?.fields?.blogPostTitle?.value}`}
          className={`rounded-md  overflow-hidden lg:w-1/2 flex flex-col group shadow-default hover:shadow-hover`}
        >
          {isEditing ? (
            <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
              <ImageWrapper
                field={secondaryArticleCard2?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </div>
          ) : (
            <Link
              href={secondaryArticleCard2?.url || ''}
              className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
            >
              <ImageWrapper
                field={secondaryArticleCard2?.fields?.heroImage}
                className="w-full h-full object-cover object-left-top"
              />
            </Link>
          )}

          <div className="flex flex-col p-12">
            <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
              {isEditing ? (
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard2?.fields?.blogContentType}
                  className=" text-blue-base font-bold "
                />
              ) : (
                <Link
                  href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${secondaryArticleCard2?.fields?.blogContentType?.value}`}
                >
                  <PlainTextWrapper
                    tag="p"
                    field={secondaryArticleCard2?.fields?.blogContentType}
                    className=" text-blue-base font-bold "
                  />
                </Link>
              )}

              <div className="text-[#111111]">
                {formatBlogDate(secondaryCard2DateString?.value)}
              </div>
            </div>

            <div className="flex justify-between">
              {isEditing ? (
                <div className="flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard2?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </div>
              ) : (
                <Link href={secondaryArticleCard2?.url || ''} className=" flex-1">
                  <PlainTextWrapper
                    tag="h3"
                    field={secondaryArticleCard2?.fields?.blogPostTitle}
                    className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                  />
                </Link>
              )}

              <SvgIcon
                icon="arrow-right"
                size="lg"
                className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              />
            </div>

            <RichTextWrapper
              field={secondaryArticleCard2?.fields?.previewText}
              className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
            />
            {(secondaryArticleCard2?.fields?.author?.fields?.authorTitle?.value !== undefined ||
              secondaryArticleCard2?.fields?.author?.fields?.authorName?.value !== undefined) && (
              <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                {isEditing ? (
                  <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                    <PlainTextWrapper
                      field={secondaryArticleCard2?.fields?.author?.fields?.authorName}
                    />
                  </div>
                ) : (
                  <Link
                    href={`${blogSearchPage?.value?.href}?authorName=${authorCardFields?.fields?.authorName?.value}`}
                    className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                  >
                    <PlainTextWrapper
                      field={secondaryArticleCard2?.fields?.author?.fields?.authorName}
                    />
                  </Link>
                )}

                {secondaryArticleCard2?.fields?.author?.fields?.authorTitle?.value !== undefined &&
                  secondaryArticleCard2?.fields?.author?.fields?.authorName?.value !== undefined &&
                  '|'}
                <PlainTextWrapper
                  tag="p"
                  field={secondaryArticleCard2?.fields?.author?.fields?.authorTitle}
                  className=""
                />
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
};

export const CondensedVersion = (props: FeaturedStoriesProps) => {
  const featuredArticle = props?.fields?.featuredArticleList as FeaturedArticleItem[];
  const isEditing = useIsEditing();
  //   Blog search page link
  const { sitecoreContext } = useSitecoreContext();
  const siteSettingsField = sitecoreContext?.siteSettings as TransformedSiteSettings;
  const blogSearchPage = siteSettingsField?.blogSearchPage as LinkField;
  //const blogLandingPage = siteSettingsField?.blogLandingPage as LinkField;

  return (
    <section className="max-w-screen-xxl mx-auto py-[80px] w-full lg:px-[80px] px-10" role="group">
      <PlainTextWrapper
        tag="h2"
        field={props?.fields?.featuredStoriesTitle}
        className="pb-12 text-36 leading-43 font-bold font-centuryGothic"
      />
      <div className="lg:grid grid-cols-3 gap-[32px]">
        {featuredArticle?.map((listItem, index) => {
          return (
            <article
              role="region"
              aria-labelledby={`${listItem?.fields?.blogPostTitle?.value}`}
              key={index}
              className="rounded-md overflow-hidden flex flex-col group shadow-default hover:shadow-hover mb-[20px] lg:mb-0 last:mb-0"
            >
              {isEditing ? (
                <div className="w-full aspect-[7/4] shrink-0 max-h-[360px]">
                  <ImageWrapper
                    field={listItem?.fields?.heroImage}
                    className="w-full h-full object-cover object-left-top"
                  />
                </div>
              ) : (
                <Link
                  href={listItem?.url || ''}
                  className="w-full aspect-[7/4] shrink-0 max-h-[360px]"
                >
                  <ImageWrapper
                    field={listItem?.fields?.heroImage}
                    className="w-full h-full object-cover object-left-top"
                  />
                </Link>
              )}
              <div className="flex flex-col p-12">
                <div className="pb-4 flex justify-between text-14 leading-[24px] font-centuryGothic">
                  {isEditing ? (
                    <PlainTextWrapper
                      tag="p"
                      field={listItem?.fields?.blogContentType}
                      className=" text-blue-base font-bold hover:text-darkblue-base "
                    />
                  ) : (
                    <Link
                      href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${listItem?.fields?.blogContentType?.value}`}
                    >
                      <PlainTextWrapper
                        tag="p"
                        field={listItem?.fields?.blogContentType}
                        className=" text-blue-base font-bold  hover:text-darkblue-base"
                      />
                    </Link>
                  )}

                  <div className="text-[#111111]">
                    {formatBlogDate(listItem?.fields?.date?.value)}
                  </div>
                </div>

                <div className="flex justify-between">
                  {isEditing ? (
                    <div className="flex-1">
                      <PlainTextWrapper
                        tag="h3"
                        field={listItem?.fields?.blogPostTitle}
                        className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                      />
                    </div>
                  ) : (
                    <Link href={listItem?.url || ''} className="flex-1">
                      <PlainTextWrapper
                        tag="h3"
                        field={listItem?.fields?.blogPostTitle}
                        className={`text-blue-base text-22 leading-31 font-bold font-centuryGothic pb-2 group-hover:underline`}
                      />
                    </Link>
                  )}

                  <SvgIcon
                    icon="arrow-right"
                    size="lg"
                    className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  />
                </div>

                <RichTextWrapper
                  field={listItem?.fields?.previewText}
                  className="py-4 text-16 leading-25 font-[500] font-nunito text-gray-900"
                />
                {(listItem?.fields?.author?.fields?.authorTitle?.value !== undefined ||
                  listItem?.fields?.author?.fields?.authorName?.value !== undefined) && (
                  <div className="flex items-center gap-[8px] text-14 leading-22 font-nunitoSans text-[#111111]">
                    {isEditing ? (
                      <div className="!text-blue-base theme-text-btn border-theme-text-btn-border text-theme-text-btn-text font-bold">
                        <PlainTextWrapper field={listItem?.fields?.author?.fields?.authorName} />
                      </div>
                    ) : (
                      <Link
                        href={`${blogSearchPage?.value?.href}?authorName=${listItem?.fields?.author?.fields?.authorName?.value}`}
                        className="!text-blue-base w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex inline font-bold font-centuryGothic gap-x-4 items-center justify-center rounded-sm lg:text-center text-left text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border"
                      >
                        <PlainTextWrapper field={listItem?.fields?.author?.fields?.authorName} />
                      </Link>
                    )}

                    {listItem?.fields?.author?.fields?.authorTitle?.value !== undefined &&
                      listItem?.fields?.author?.fields?.authorName?.value !== undefined &&
                      '|'}

                    <PlainTextWrapper
                      tag="p"
                      field={listItem?.fields?.author?.fields?.authorTitle}
                      className=""
                    />
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {featuredArticle?.length !== 0 && (
        //datasource based link
        <LinkWrapper
          ctaVariant="link"
          ctaIcon="arrow-right"
          className="font-semibold text-18 leading-25 text-blue-base font-centuryGothic hover:text-darkblue-base focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline rounded-sm flex items-center mt-[32px] py-[14px] gap-[8px] w-fit"
          field={props?.fields?.viewMoreLink}
        />
        //removed link from sitesetting as per change request - CW-1375
        // <>
        //   {isEditing ? (
        //     <div className="font-semibold text-18 leading-25 text-blue-base font-centuryGothic hover:text-darkblue-base focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline rounded-sm flex items-center mt-[32px] py-[14px] gap-[8px] w-fit">
        //       <span>View more stories</span>
        //       <SvgIcon icon="arrow-right" size="md" className="text-blue-base" />
        //     </div>
        //   ) : (
        //     <Link
        //       href={blogLandingPage?.value?.href || ''}
        //       className="font-semibold text-18 leading-25 text-blue-base font-centuryGothic hover:text-darkblue-base focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline rounded-sm flex items-center mt-[32px] py-[14px] gap-[8px] w-fit"
        //     >
        //       <span>View more stories</span>
        //       <SvgIcon icon="arrow-right" size="md" className="text-blue-base" />
        //     </Link>
        //   )}
        // </>
      )}
    </section>
  );
};
